import React, { useEffect, useState } from 'react';
import { 
  Mail, Phone, MapPin, Send, MessageSquare, 
  Clock, ShieldCheck, Globe, Loader2, CheckCircle, MessageCircle 
} from 'lucide-react';
import SEO from './seo/SEO';
import { db } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { sendNotificationEmail } from './utils/emailService';

const ContactPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [status, setStatus] = useState('idle'); // idle, loading, success, error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      // 1. Store directly in Firebase Firestore DB
      await addDoc(collection(db, 'enquiries'), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        message: formData.message,
        status: 'New',
        createdAt: serverTimestamp(),
        createdAtString: new Date().toLocaleString()
      });

      // 2. Dispatch email to both globaleprc@gmail.com & jebincocjebnjebin232@gmail.com
      await sendNotificationEmail({
        formType: 'Website Contact Enquiry',
        subject: `[Contact Enquiry] New Message from ${formData.name}`,
        data: {
          Name: formData.name,
          Email: formData.email,
          Phone: formData.phone || 'Not provided',
          Message: formData.message
        }
      });
      
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error("Submission error:", error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col pt-32">
      <SEO
        title="Contact GlobalEPRC | Global Ethical People Resourcing Consultancy"
        description="Get in touch with GlobalEPRC for recruitment, outsourcing, IT solutions, skill development and travel & tourism enquiries. Call, email or send us a message."
        path="/contact"
      />
      <main className="flex-grow">
        <div className="container mx-auto px-4 md:px-8 mb-20">
          
          {/* Header Section */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-gold-gradient font-bold uppercase tracking-[0.3em] text-sm mb-4">Contact Us</h2>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">
              GlobalEPRC – Your Bridge to Right Talent
            </h1>
            <p className="text-slate-600 text-lg">
              Have questions about our recruitment, outsourcing, or travel services? 
              Our team is here to provide the support you need.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-12 items-start">
            
            {/* LEFT: Contact Information Cards */}
            <div className="lg:w-2/5 w-full space-y-6">
              {/* WhatsApp Card */}
              <a 
                href="https://wa.me/918610452001?text=Hi%20GlobalEPRC%2C%20I%20would%20like%20to%20get%20in%20touch." 
                target="_blank" 
                rel="noopener noreferrer"
                className="block bg-white p-8 rounded-3xl shadow-sm border border-emerald-100 hover:border-emerald-400 group transition-all"
              >
                <div className="flex items-center gap-6">
                  <div className="bg-emerald-50 p-4 rounded-2xl group-hover:bg-emerald-500 group-hover:text-white transition-all text-emerald-600">
                    <MessageCircle className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-emerald-700 text-xs font-bold uppercase tracking-widest">WhatsApp Text</h4>
                      <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold">Text Us Here</span>
                    </div>
                    <p className="text-slate-900 font-black text-lg">+91 8610452001</p>
                    <p className="text-xs text-slate-500 mt-0.5">Click here to send WhatsApp text</p>
                  </div>
                </div>
              </a>

              {/* Email Card */}
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 group hover:border-amber-200 transition-all">
                <div className="flex items-center gap-6">
                  <div className="bg-amber-50 p-4 rounded-2xl group-hover:bg-gold-gradient group-hover:text-white transition-all">
                    <Mail className="h-6 w-6 text-amber-600 group-hover:text-white" />
                  </div>
                  <div>
                    <h4 className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">Email Us</h4>
                    <p className="text-slate-900 font-bold text-base break-all">ethical.recruitment@globaleprc.com</p>
                    <p className="text-slate-600 font-medium text-xs break-all mt-1">globaleprc@gmail.com</p>
                  </div>
                </div>
              </div>

              {/* Call Card */}
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 group hover:border-amber-200 transition-all">
                <div className="flex items-center gap-6">
                  <div className="bg-amber-50 p-4 rounded-2xl group-hover:bg-gold-gradient group-hover:text-white transition-all">
                    <Phone className="h-6 w-6 text-amber-600 group-hover:text-white" />
                  </div>
                  <div>
                    <h4 className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">Call Us</h4>
                    <p className="text-slate-900 font-bold text-lg">+91- 96060 23500</p>
                    <p className="text-slate-900 font-bold text-lg">+91- 96060 23501</p>
                  </div>
                </div>
              </div>

              {/* Address Card */}
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 group hover:border-amber-200 transition-all">
                <div className="flex items-center gap-6">
                  <div className="bg-amber-50 p-4 rounded-2xl group-hover:bg-gold-gradient group-hover:text-white transition-all">
                    <MapPin className="h-6 w-6 text-amber-600 group-hover:text-white" />
                  </div>
                  <div>
                    <h4 className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">Our Address</h4>
                    <p className="text-slate-900 font-bold text-lg">Bangalore, India</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900 p-8 rounded-[2rem] text-white relative overflow-hidden mt-10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold-gradient opacity-10 rounded-full -mr-16 -mt-16"></div>
                <h4 className="font-bold text-xl mb-4 text-gold-gradient">Why reach out?</h4>
                <ul className="space-y-3 text-sm text-slate-300">
                  <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-500" /> Ethical Recruitment Focus</li>
                  <li className="flex items-center gap-2"><Globe className="h-4 w-4 text-amber-500" /> Global Network Support</li>
                  <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-amber-500" /> 24/7 Service Reliability</li>
                </ul>
              </div>
            </div>

            {/* RIGHT: Enquiry Form */}
            <div className="lg:w-3/5 w-full bg-white p-8 md:p-12 rounded-[2.5rem] shadow-xl border border-slate-100 relative">
              {status === 'success' ? (
                <div className="text-center py-12 animate-in zoom-in">
                  <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
                  <h3 className="text-3xl font-black text-slate-900 mb-4">Message Sent!</h3>
                  <p className="text-slate-600 mb-8">Thank you for contacting GlobalEPRC. Our team will get back to you shortly.</p>
                  <button 
                    onClick={() => setStatus('idle')}
                    className="text-amber-600 font-bold hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-slate-900 mb-8 flex items-center gap-3">
                    <MessageSquare className="text-amber-600" /> Send an Enquiry
                  </h3>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-bold text-slate-500 uppercase ml-1">Full Name</label>
                        <input 
                          type="text" 
                          required
                          value={formData.name}
                          placeholder="Enter your name"
                          className="p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all"
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-bold text-slate-500 uppercase ml-1">Email Address</label>
                        <input 
                          type="email" 
                          required
                          value={formData.email}
                          placeholder="email@company.com"
                          className="p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all"
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold text-slate-500 uppercase ml-1">Phone Number</label>
                      <input 
                        type="tel" 
                        value={formData.phone}
                        placeholder="+91 00000 00000"
                        className="p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all"
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-bold text-slate-500 uppercase ml-1">Your Message</label>
                      <textarea 
                        rows="5"
                        required
                        value={formData.message}
                        placeholder="How can we help you?"
                        className="p-4 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all"
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                      ></textarea>
                    </div>

                    {status === 'error' && (
                      <p className="text-red-500 text-sm font-bold">Something went wrong. Please try again later.</p>
                    )}

                    <button 
                      type="submit"
                      disabled={status === 'loading'}
                      className="w-full bg-gold-gradient py-5 rounded-2xl font-black text-lg flex items-center justify-center gap-3 shadow-lg hover:opacity-90 transition-all mt-4 disabled:opacity-50"
                    >
                      {status === 'loading' ? (
                        <>Sending... <Loader2 className="h-5 w-5 animate-spin" /></>
                      ) : (
                        <>Submit Enquiry <Send className="h-5 w-5" /></>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;
